import React, { useEffect, useState } from "react";
import { Button, Card, Col, Container, Row, Table } from "react-bootstrap";
import axiosInstance from "../../utils/axiosUtil";
import LoadingBox from "../layout/LoadingBox";
import MessageBox from "../layout/MessageBox";
import EditPlan from "./EditPlan";

export default function Plans() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [editModal, setEditModal] = useState({
    show: false,
    plan: null
  });

  const loadPlans = async () => {
    setLoading(true);
    setError("");
    setActionError("");
    setActionMessage("");
    try {
      const { data } = await axiosInstance.get("/plans/all");
      console.log("Plans fetched:", data);
      setPlans(Array.isArray(data) ? data : []);
      if (!Array.isArray(data)) {
        setError("Invalid response format from server");
      } else if (data.length === 0) {
        setError("No plans found. Please run the seed script to create plans.");
      }
    } catch (err) {
      console.error("Error loading plans:", err);
      const errorMessage = err?.response?.data?.message || err?.message || "Failed to load plans";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlans();
  }, []);

  const openEditModal = (plan) => {
    setEditModal({
      show: true,
      plan: plan
    });
    setActionError("");
    setActionMessage("");
  };

  const closeEditModal = () => {
    setEditModal({
      show: false,
      plan: null
    });
  };

  const handlePlanUpdated = (updatedPlan) => {
    setPlans((prev) =>
      prev.map((item) =>
        item._id === updatedPlan._id ? updatedPlan : item
      )
    );
    setActionMessage("Plan updated successfully.");
    closeEditModal();
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Pricing Plans</h4>
              <Button variant="primary" onClick={loadPlans}>
                Refresh
              </Button>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <LoadingBox />
              ) : error ? (
                <MessageBox variant="danger">{error}</MessageBox>
              ) : (
                <>
                  {actionError && (
                    <MessageBox variant="danger" dismissible onClose={() => setActionError("")}>
                      {actionError}
                    </MessageBox>
                  )}
                  {actionMessage && (
                    <MessageBox variant="success" dismissible onClose={() => setActionMessage("")}>
                      {actionMessage}
                    </MessageBox>
                  )}

                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Plan Name</th>
                        <th>Total Price</th>
                        <th>Discounted Price</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plans.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center">
                            No plans found. Please run the seed script to create plans.
                          </td>
                        </tr>
                      ) : (
                        plans.map((plan) => (
                          <tr key={plan._id}>
                            <td>{plan._id}</td>
                            <td>
                              <strong>{plan.name}</strong>
                            </td>
                            <td>${plan.totalPrice?.toFixed(2)}</td>
                            <td>
                              <strong className="text-success">${plan.discountedPrice?.toFixed(2)}</strong>
                            </td>
                            <td>
                              <span
                                className={`badge ${plan.isActive ? "bg-success" : "bg-secondary"}`}
                              >
                                {plan.isActive ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td>
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => openEditModal(plan)}
                              >
                                Edit
                              </Button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <EditPlan
        show={editModal.show}
        plan={editModal.plan}
        onClose={closeEditModal}
        onUpdated={handlePlanUpdated}
        onError={(errorMsg) => setActionError(errorMsg)}
      />
    </Container>
  );
}

